import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import MultiSelect from "@/components/MultiSelect";
import { JOB_TYPE_LABELS } from "@/lib/helpers";
import { Trash2, Mail, Users, Boxes, LifeBuoy, UserPlus, AlertTriangle } from "lucide-react";

const PIPELINE_STAGES = [
  { key: "cold", label: "Cold" }, { key: "warm", label: "Warm" }, { key: "hot", label: "Hot" },
  { key: "quoted", label: "Quoted" }, { key: "approved", label: "Approved" }, { key: "scheduled", label: "Scheduled" },
  { key: "completed", label: "Completed" }, { key: "invoiced", label: "Invoiced" }, { key: "archived", label: "Archived" }, { key: "deleted", label: "Cancelled" },
];

const toLocalInput = (iso) => {
  try {
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60000);
    return local.toISOString().slice(0, 16);
  } catch { return ""; }
};

export default function JobForm({ initial, onSaved, onClose }) {
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ drone_types: [], job_types: [], statuses: [] });
  const [employees, setEmployees] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assetGroups, setAssetGroups] = useState([]);
  const [clients, setClients] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [relatedEmails, setRelatedEmails] = useState([]);
  const [form, setForm] = useState({
    title: "", client_name: "", client_id: "", start: "", end: "", location: "",
    drone_type: "", job_type: "survey", employee_id: "", assistant_ids: [], status: "scheduled", pipeline_stage: "scheduled",
    notes: "", source: "manual", avcrm_loaded: false, asset_id: "", asset_ids: [], asset_group_ids: [],
    sar_name: "", sar_phone: "", sar_notes: "",
  });

  const dateKey = form.start ? String(form.start).slice(0, 10) : null;
  const unavailById = useMemo(() => {
    const m = {};
    if (!dateKey) return m;
    availability.forEach((a) => { const end = a.end || a.start; if (a.start <= dateKey && dateKey <= end) m[a.employee_id] = a.type; });
    return m;
  }, [availability, dateKey]);
  const assignedUnavailable = [form.employee_id, ...(form.assistant_ids || [])]
    .filter((id) => id && unavailById[id])
    .map((id) => ({ id, name: employees.find((e) => e.id === id)?.name || "Crew", type: unavailById[id] }));
  const dncAssigned = assignedUnavailable.filter((c) => c.type === "dnc");

  const assistantOptions = useMemo(
    () => employees.filter((e) => e.id !== form.employee_id).map((e) => ({ value: e.id, label: unavailById[e.id] ? `${e.name} — unavailable${unavailById[e.id] === "dnc" ? " (do not contact)" : ""}` : e.name })),
    [employees, form.employee_id, unavailById]
  );

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
    api.get("/assets").then((r) => setAssets(r.data)).catch(() => {});
    api.get("/asset-groups").then((r) => setAssetGroups(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/availability").then((r) => setAvailability(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (initial?.id) {
      api.get(`/jobs/${initial.id}/emails`).then((r) => setRelatedEmails(r.data)).catch(() => {});
    } else {
      setRelatedEmails([]);
    }
  }, [initial?.id]);

  useEffect(() => {
    if (initial) {
      setForm((f) => ({
        ...f,
        ...initial,
        start: toLocalInput(initial.start),
        end: toLocalInput(initial.end),
        employee_id: initial.employee_id || "",
        assistant_ids: initial.assistant_ids || [],
        source: initial.source || "manual",
        avcrm_loaded: !!initial.avcrm_loaded,
        asset_id: initial.asset_id || "",
        asset_ids: initial.asset_ids || (initial.asset_id ? [initial.asset_id] : []),
        asset_group_ids: initial.asset_group_ids || [],
        client_id: initial.client_id || "",
        sar_name: initial.sar_name || "",
        sar_phone: initial.sar_phone || "",
        sar_notes: initial.sar_notes || "",
      }));
    }
  }, [initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.title || !form.start) {
      toast.error("Title and start time are required");
      return;
    }
    if (dncAssigned.length > 0) {
      setConfirmOpen(true);
      return;
    }
    await doSave();
  };

  const doSave = async () => {
    setSaving(true);
    const clientObj = clients.find((c) => c.id === form.client_id);
    const payload = {
      ...form,
      client_name: clientObj ? clientObj.name : form.client_name,
      start: new Date(form.start).toISOString(),
      end: form.end ? new Date(form.end).toISOString() : null,
      employee_id: form.employee_id || null,
      asset_id: form.asset_ids?.[0] || null,
    };
    try {
      if (initial?.id) {
        await api.put(`/jobs/${initial.id}`, payload);
        toast.success("Job updated");
      } else {
        await api.post("/jobs", payload);
        toast.success("Job created");
      }
      window.dispatchEvent(new Event("jobs:refresh"));
      onSaved?.();
    } catch (e) {
      toast.error("Failed to save job");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!initial?.id) return;
    setSaving(true);
    try {
      await api.delete(`/jobs/${initial.id}`);
      toast.success("Job deleted");
      window.dispatchEvent(new Event("jobs:refresh"));
      onSaved?.();
    } catch (e) {
      toast.error("Failed to delete");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="job-form">
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Job Title</Label>
        <Input data-testid="job-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Sugarcane spraying" className="mt-1.5" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Client</Label>
            <button type="button" onClick={() => navigate("/clients")} data-testid="job-manage-clients-link"
              className="text-[11px] font-medium text-lime-600 hover:text-lime-700 inline-flex items-center gap-0.5">
              <UserPlus className="h-3 w-3" /> Manage
            </button>
          </div>
          <Select value={form.client_id} onValueChange={(v) => set("client_id", v)}>
            <SelectTrigger data-testid="job-client-select" className="mt-1.5"><SelectValue placeholder="Select client" /></SelectTrigger>
            <SelectContent>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.contact_name ? ` · ${c.contact_name}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Location</Label>
          <Input data-testid="job-location-input" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Site / town" className="mt-1.5" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Start</Label>
          <Input data-testid="job-start-input" type="datetime-local" value={form.start} onChange={(e) => set("start", e.target.value)} className="mt-1.5" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">End</Label>
          <Input data-testid="job-end-input" type="datetime-local" value={form.end} onChange={(e) => set("end", e.target.value)} className="mt-1.5" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Job Type</Label>
          <Select value={form.job_type} onValueChange={(v) => set("job_type", v)}>
            <SelectTrigger data-testid="job-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              {meta.job_types.map((t) => <SelectItem key={t} value={t}>{JOB_TYPE_LABELS[t] || t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Status</Label>
          <Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger data-testid="job-status-select" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              {meta.statuses.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Pipeline Stage</Label>
        <Select value={form.pipeline_stage} onValueChange={(v) => set("pipeline_stage", v)}>
          <SelectTrigger data-testid="job-stage-select" className="mt-1.5"><SelectValue placeholder="Select stage" /></SelectTrigger>
          <SelectContent>
            {PIPELINE_STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Drone</Label>
          <Select value={form.drone_type} onValueChange={(v) => set("drone_type", v)}>
            <SelectTrigger data-testid="job-drone-select" className="mt-1.5"><SelectValue placeholder="Select drone" /></SelectTrigger>
            <SelectContent>
              {meta.drone_types.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Pilot in Command (PIC)</Label>
          <Select value={form.employee_id} onValueChange={(v) => set("employee_id", v)}>
            <SelectTrigger data-testid="job-employee-select" className="mt-1.5"><SelectValue placeholder="Assign PIC" /></SelectTrigger>
            <SelectContent>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.name}{unavailById[e.id] ? (unavailById[e.id] === "dnc" ? " — unavailable (do not contact)" : " — unavailable (ok to contact)") : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Assistants / Crew</Label>
        <MultiSelect
          testid="job-assistants"
          placeholder="Add crew members"
          value={form.assistant_ids}
          onChange={(v) => set("assistant_ids", v)}
          options={assistantOptions}
        />
      </div>

      {dateKey && assignedUnavailable.length > 0 && (
        <div data-testid="job-availability-warning" className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2.5">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold">Crew unavailable on this date</p>
            <ul className="mt-0.5 space-y-0.5">
              {assignedUnavailable.map((c) => (
                <li key={c.id} data-testid={`job-unavailable-${c.id}`}>
                  {c.name} — {c.type === "dnc" ? "on leave / do not contact" : "flagged (ok to contact)"}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Notes</Label>
        <Textarea data-testid="job-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Extra details" className="mt-1.5" rows={2} />
      </div>

      <label className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 cursor-pointer">
        <Checkbox
          data-testid="job-avcrm-checkbox"
          checked={form.avcrm_loaded}
          onCheckedChange={(v) => set("avcrm_loaded", !!v)}
          className="data-[state=checked]:bg-lime-500 data-[state=checked]:border-lime-500"
        />
        <span className="text-sm font-medium text-slate-700">Loaded to AVCRM</span>
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Boxes className="h-3.5 w-3.5" /> Assigned Assets</Label>
          <MultiSelect
            testid="job-assets"
            placeholder="Assign assets"
            value={form.asset_ids}
            onChange={(v) => set("asset_ids", v)}
            options={assets.map((a) => ({ value: a.id, label: a.name, sub: a.registration || a.model }))}
          />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Boxes className="h-3.5 w-3.5" /> Asset Groups</Label>
          <MultiSelect
            testid="job-asset-groups"
            placeholder="Assign asset groups"
            value={form.asset_group_ids}
            onChange={(v) => set("asset_group_ids", v)}
            options={assetGroups.map((g) => ({ value: g.id, label: g.name, sub: g.description }))}
          />
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="job-sar-section">
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><LifeBuoy className="h-3.5 w-3.5 text-orange-500" /> SAR / Emergency Contact</Label>
        <p className="text-[11px] text-slate-400 -mt-1">Leave blank to use the global default SAR contact from Settings.</p>
        <div className="grid grid-cols-2 gap-3">
          <Input data-testid="job-sar-name-input" value={form.sar_name} onChange={(e) => set("sar_name", e.target.value)} placeholder="Contact name" />
          <Input data-testid="job-sar-phone-input" value={form.sar_phone} onChange={(e) => set("sar_phone", e.target.value)} placeholder="Phone" />
        </div>
        <Input data-testid="job-sar-notes-input" value={form.sar_notes} onChange={(e) => set("sar_notes", e.target.value)} placeholder="Notes (e.g. flight following, area)" />
      </div>

      {relatedEmails.length > 0 && (
        <div data-testid="job-related-emails">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Related Emails ({relatedEmails.length})</Label>
          <div className="mt-2 space-y-2">
            {relatedEmails.map((em) => (
              <div key={em.id} className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                <p className="text-sm font-medium text-slate-800 truncate">{em.subject}</p>
                <p className="text-xs text-slate-500 truncate">{em.from_name} &lt;{em.from_email}&gt;</p>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{em.snippet}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 pt-2">
        <Button data-testid="save-job-btn" onClick={handleSave} disabled={saving} className="flex-1 bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold">
          {saving ? "Saving…" : initial?.id ? "Update Job" : "Create Job"}
        </Button>
        {initial?.id && (
          <Button data-testid="delete-job-btn" onClick={handleDelete} disabled={saving} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="job-dnc-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" /> Assign crew on leave?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The following crew are marked <span className="font-semibold">do not contact</span> on{" "}
              {dateKey ? new Date(dateKey).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "this date"}:
              <ul className="mt-2 list-disc pl-5 text-slate-700">
                {dncAssigned.map((c) => <li key={c.id}>{c.name}</li>)}
              </ul>
              <span className="mt-2 block">Are you sure you want to assign them to this job?</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="job-dnc-cancel">Go back</AlertDialogCancel>
            <AlertDialogAction data-testid="job-dnc-confirm-btn" onClick={doSave}
              className="bg-amber-500 hover:bg-amber-400 text-white">
              Assign anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
